import { addMilliseconds } from "date-fns";

import { PresenceClient } from "@formant/core/client/PresenceClient";
import { intervalToDuration } from "@formant/core/common/intervalToDuration";
import { IIntervalQuery } from "@formant/core/model/IIntervalQuery";

import { IPresenceInterval } from "./IPresenceInterval";
import { PresenceSegmentCache } from "./PresenceSegmentCache";

export class PresenceCache {
    private minutePresenceCache!: PresenceSegmentCache;
    private hourPresenceCache!: PresenceSegmentCache;
    private dayPresenceCache!: PresenceSegmentCache;

    constructor(presenceClient: PresenceClient) {
        this.minutePresenceCache = new PresenceSegmentCache(
            "minute",
            presenceClient,
        );
        this.hourPresenceCache = new PresenceSegmentCache(
            "hour",
            presenceClient,
        );
        this.dayPresenceCache = new PresenceSegmentCache("day", presenceClient);
    }

    public async query(
        token: string,
        query: IIntervalQuery,
    ): Promise<IPresenceInterval[]> {
        const { interval } = query;
        const results = await this.doQuery(token, query);
        return mergePresence(
            results.map(result => ({
                start: result,
                end: addMilliseconds(result, intervalToDuration(interval)),
            })),
        );
    }

    private doQuery(token: string, query: IIntervalQuery): Promise<Date[]> {
        const { interval } = query;
        switch (interval) {
            case "minute":
                return this.minutePresenceCache.query(token, query);
            case "hour":
                return this.hourPresenceCache.query(token, query);
            case "day":
                return this.dayPresenceCache.query(token, query);
            default:
                throw Error("unexpected query interval");
        }
    }
}

function mergePresence(
    presenceIntervals: IPresenceInterval[],
): IPresenceInterval[] {
    return presenceIntervals
        .slice()
        .sort((a, b) => a.start.getTime() - b.start.getTime())
        .reduce<IPresenceInterval[]>((acc, current) => {
            const [last, ...rest] = acc;

            if (!last) {
                return [current];
            } else if (current.start.getTime() === last.end.getTime()) {
                return [{ start: last.start, end: current.end }, ...rest];
            } else {
                return [current, last, ...rest];
            }
        }, [])
        .reverse();
}
